import { ApiError, apiErrorFromResponse } from './errors';
function joinUrl(baseUrl, endpoint) {
    const normalizedBase = baseUrl.replace(/\/+$/, '');
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${normalizedBase}${normalizedEndpoint}`;
}
function isFormData(body) {
    return typeof FormData !== 'undefined' && body instanceof FormData;
}
function buildHeaders(options, accessToken, includeAuth) {
    const headers = new Headers(options.headers);
    if (!headers.has('Content-Type') && options.body !== undefined && !isFormData(options.body)) {
        headers.set('Content-Type', 'application/json');
    }
    if (includeAuth && accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }
    else if (!includeAuth) {
        headers.delete('Authorization');
    }
    return headers;
}
export function createSessionClient(options) {
    const refreshEndpoint = options.refreshEndpoint ?? '/auth/token/refresh/';
    const fetchFn = options.fetchFn
        ?? ((input, init) => globalThis.fetch(input, init));
    let refreshInFlight = null;
    let sessionExpiredNotified = false;
    async function performRefresh() {
        const refreshToken = options.tokenStore.getRefreshToken();
        if (!refreshToken)
            return 'rejected';
        try {
            const response = await fetchFn(joinUrl(options.baseUrl, refreshEndpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken }),
            });
            if (!response.ok) {
                return response.status === 400 || response.status === 401
                    ? 'rejected'
                    : 'unavailable';
            }
            const payload = (await response.json());
            if (typeof payload.access !== 'string' || payload.access.length === 0) {
                return 'unavailable';
            }
            options.tokenStore.setTokens(payload.access, typeof payload.refresh === 'string' ? payload.refresh : undefined);
            sessionExpiredNotified = false;
            return 'refreshed';
        }
        catch {
            return 'unavailable';
        }
    }
    async function refresh() {
        if (!refreshInFlight) {
            refreshInFlight = performRefresh().finally(() => {
                refreshInFlight = null;
            });
        }
        return refreshInFlight;
    }
    function expireSession() {
        if (!sessionExpiredNotified) {
            sessionExpiredNotified = true;
            options.tokenStore.clearTokens();
            options.onSessionExpired?.();
        }
    }
    async function fetchWithSession(endpoint, requestOptions = {}) {
        const { auth = true, retryAuth = true, ...fetchOptions } = requestOptions;
        const url = joinUrl(options.baseUrl, endpoint);
        let sentAccessToken = null;
        const send = () => {
            const accessToken = options.tokenStore.getAccessToken();
            sentAccessToken = accessToken;
            if (auth && accessToken)
                sessionExpiredNotified = false;
            return fetchFn(url, {
                ...fetchOptions,
                headers: buildHeaders(fetchOptions, accessToken, auth),
            });
        };
        let response = await send();
        const isRefreshRequest = url === joinUrl(options.baseUrl, refreshEndpoint);
        if (response.status !== 401 || !auth || !retryAuth || isRefreshRequest) {
            return response;
        }
        const currentAccessToken = options.tokenStore.getAccessToken();
        if (currentAccessToken && currentAccessToken !== sentAccessToken) {
            response = await send();
            if (response.status !== 401)
                return response;
            expireSession();
            throw new ApiError(401, 'Session expired', 'SESSION_EXPIRED');
        }
        const refreshResult = await refresh();
        if (refreshResult === 'refreshed') {
            response = await send();
            if (response.status !== 401)
                return response;
        }
        if (refreshResult === 'unavailable') {
            throw new ApiError(0, 'Unable to refresh session. Check your connection and retry.', 'TOKEN_REFRESH_UNAVAILABLE');
        }
        expireSession();
        throw new ApiError(401, 'Session expired', 'SESSION_EXPIRED');
    }
    async function request(endpoint, requestOptions = {}) {
        const response = await fetchWithSession(endpoint, requestOptions);
        if (!response.ok)
            throw await apiErrorFromResponse(response);
        if (response.status === 204)
            return undefined;
        return response.json();
    }
    return {
        request,
        raw: async (endpoint, requestOptions = {}) => {
            const response = await fetchWithSession(endpoint, requestOptions);
            if (!response.ok)
                throw await apiErrorFromResponse(response);
            return response;
        },
        get: (endpoint, requestOptions = {}) => request(endpoint, requestOptions),
        post: (endpoint, body, requestOptions = {}) => request(endpoint, {
            ...requestOptions,
            method: 'POST',
            body: body === undefined ? undefined : JSON.stringify(body),
        }),
        patch: (endpoint, body, requestOptions = {}) => request(endpoint, {
            ...requestOptions,
            method: 'PATCH',
            body: JSON.stringify(body),
        }),
        delete: (endpoint, requestOptions = {}) => request(endpoint, {
            ...requestOptions,
            method: 'DELETE',
        }),
        refreshAccessToken: async () => {
            const result = await refresh();
            if (result === 'rejected')
                expireSession();
            return result === 'refreshed';
        },
    };
}
//# sourceMappingURL=session-client.js.map